// super-excalidraw: animated GIF overlay layer.
//
// canvas.drawImage() only captures the first frame of a GIF — the image
// element on the Excalidraw canvas is forever frozen. This component renders
// `<img>` elements positioned over the canvas via sceneCoordsToViewportCoords
// so GIFs actually animate, including through pan, zoom, and rotation.
//
// Mounted as a child of <Excalidraw> (same pattern as CommentsLayer) so it
// sits in the correct stacking context. pointer-events: none ensures it never
// blocks canvas interaction.
//
// Frame-aware clipping: a GIF that belongs to a frame (el.frameId) is clipped
// to that frame's viewport rect so it behaves like a real framed image —
// resizing the frame reflows the visible area, and the GIF never spills past
// the frame edges. Frames in Excalidraw are axis-aligned, so the clip is a
// simple overflow:hidden box.

import React, { useEffect, useRef, useState } from "react";

import { sceneCoordsToViewportCoords } from "@excalidraw/common";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import "./AnimatedGifLayer.scss";

type Overlay = {
  id: string;
  src: string;
  left: number;
  top: number;
  width: number;
  height: number;
  angle: number;
  opacity: number;
  // When the GIF belongs to a frame, the frame's viewport rect to clip to.
  clip: { left: number; top: number; width: number; height: number } | null;
};

export const AnimatedGifLayer: React.FC<{
  excalidrawAPI: ExcalidrawImperativeAPI;
}> = ({ excalidrawAPI }) => {
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  // Cache data URLs by fileId so string references stay stable between ticks.
  const srcCache = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    const recompute = () => {
      const elements = excalidrawAPI.getSceneElements() as any[];
      const appState = excalidrawAPI.getAppState() as any;
      const files = excalidrawAPI.getFiles();
      const zoom: number = appState.zoom?.value ?? 1;

      // Fast-path: if there are no GIF files at all, skip element scan.
      let hasGif = false;
      for (const f of Object.values(files)) {
        if ((f as any).mimeType === "image/gif") {
          hasGif = true;
          break;
        }
      }
      if (!hasGif) {
        setOverlays((prev) => (prev.length === 0 ? prev : []));
        return;
      }

      // Index frames by id so a framed GIF can look up its clip rect.
      const framesById = new Map<string, any>();
      for (const el of elements) {
        if (!el.isDeleted && el.type === "frame") {
          framesById.set(el.id, el);
        }
      }

      const next: Overlay[] = [];
      for (const el of elements) {
        if (el.isDeleted || el.type !== "image") continue;
        const file = (files as any)[el.fileId];
        if (!file || file.mimeType !== "image/gif") continue;

        let src = srcCache.current.get(el.fileId);
        if (!src) {
          src = file.dataURL as string;
          if (!src) continue;
          srcCache.current.set(el.fileId, src);
        }

        const tl = sceneCoordsToViewportCoords(
          { sceneX: el.x, sceneY: el.y },
          appState,
        );

        // If the GIF is in a frame, compute the frame's viewport rect to clip
        // against. Frames are axis-aligned so this is a plain box.
        let clip: Overlay["clip"] = null;
        const frame = el.frameId ? framesById.get(el.frameId) : null;
        if (frame) {
          const ftl = sceneCoordsToViewportCoords(
            { sceneX: frame.x, sceneY: frame.y },
            appState,
          );
          clip = {
            left: ftl.x,
            top: ftl.y,
            width: frame.width * zoom,
            height: frame.height * zoom,
          };
        }

        next.push({
          id: el.id,
          src,
          left: tl.x,
          top: tl.y,
          width: el.width * zoom,
          height: el.height * zoom,
          angle: el.angle ?? 0,
          opacity: (el.opacity ?? 100) / 100,
          clip,
        });
      }

      setOverlays(next);
    };

    recompute();
    const off = excalidrawAPI.onChange(() => recompute());
    return () => off();
  }, [excalidrawAPI]);

  if (overlays.length === 0) return null;

  return (
    <div className="gif-layer">
      {overlays.map((o) =>
        o.clip ? (
          // Clip container at the frame rect; the GIF is positioned relative to
          // it and overflow:hidden trims anything past the frame edge.
          <div
            key={o.id}
            className="gif-layer__clip"
            style={{
              left: o.clip.left,
              top: o.clip.top,
              width: o.clip.width,
              height: o.clip.height,
            }}
          >
            <img
              src={o.src}
              alt=""
              className="gif-layer__img"
              style={{
                left: o.left - o.clip.left,
                top: o.top - o.clip.top,
                width: o.width,
                height: o.height,
                opacity: o.opacity,
                transform: o.angle ? `rotate(${o.angle}rad)` : undefined,
              }}
            />
          </div>
        ) : (
          <img
            key={o.id}
            src={o.src}
            alt=""
            className="gif-layer__img"
            style={{
              left: o.left,
              top: o.top,
              width: o.width,
              height: o.height,
              opacity: o.opacity,
              transform: o.angle ? `rotate(${o.angle}rad)` : undefined,
            }}
          />
        ),
      )}
    </div>
  );
};
